import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { friendlyAuthError } from "@/lib/auth-errors";

export const Route = createFileRoute("/reset-password")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Reset your password · Buddha KA" },
      { name: "description", content: "Choose a new password for your Buddha KA account." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ResetPasswordPage,
});

type State = "checking" | "ready" | "invalid" | "done";

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<State>("checking");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    // Supabase delivers the recovery session either in the URL hash
    // (implicit flow) or as an already-restored session.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") setState("ready");
    });

    (async () => {
      const hash = typeof window !== "undefined" ? window.location.hash : "";
      if (hash.includes("error")) {
        if (alive) setState("invalid");
        return;
      }
      const { data } = await supabase.auth.getSession();
      if (!alive) return;
      setState(data.session ? "ready" : hash.includes("access_token") ? "checking" : "invalid");
    })();

    const timer = setTimeout(() => {
      if (alive) setState((s) => (s === "checking" ? "invalid" : s));
    }, 4000);

    return () => {
      alive = false;
      clearTimeout(timer);
      sub.subscription.unsubscribe();
    };
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Both passwords must match.");
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setState("done");
      setTimeout(() => navigate({ to: "/account" }), 1500);
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-md flex-col justify-center px-4 py-16 sm:px-6">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">Recover access</p>
      <h1 className="mt-3 font-serif text-4xl">Set a new password.</h1>

      {state === "checking" && (
        <p className="mt-6 text-sm text-muted-foreground">Verifying your reset link…</p>
      )}

      {state === "invalid" && (
        <div className="mt-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            This reset link is invalid, already used, or expired. Request a fresh one and open it on
            the same device.
          </p>
          <a
            href="/auth?mode=forgot"
            className="inline-flex rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian"
          >
            Send a new reset link
          </a>
        </div>
      )}

      {state === "done" && (
        <p className="mt-6 text-sm text-emerald-400">
          Password updated. Taking you to your account…
        </p>
      )}

      {state === "ready" && (
        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">New password</span>
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm outline-none focus:border-gold"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Confirm password</span>
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-2 w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm outline-none focus:border-gold"
            />
          </label>
          {error && <p className="text-sm text-red-400">{error}</p>}
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian disabled:opacity-60"
          >
            {busy ? "Updating…" : "Update password"}
          </button>
        </form>
      )}
    </div>
  );
}
